import React, { useState, useEffect } from 'react'
import { useDispatch, connect } from 'react-redux'
import RootState from 'typings/RootState'
import styled from 'styled-components'
import { RouteComponentProps } from 'react-router-dom'
import { BreadcrumbLink } from 'Components/Shared/BreadCrumb/BreadCrumb'
import SubnavBar from 'Components/Shared/SubnavBar'
import { HeaderRow } from 'Components/Shared/Table'
import Pagination from 'Components/Shared/Pagination'
import { PaginationType } from 'features/types/common'
import {
  userEmailSelector,
  serviceTokenSelector,
} from 'features/sensitive/sensitive.selector'

import {
  getReportList,
  getPercentPMR,
  updatePercentPMR,
  exportReportList,
} from 'features/adminReport/certificate/api'
import { sortingTask } from 'features/adminReport/certificate/actions'
import {
  GetReportListReq,
  DataItem,
  GetPercentPMRReq,
  UpdatePercentPMRReq,
} from 'features/adminReport/certificate/types'

import Menu from 'Components/MenuAdmin/AdminQCMenu'
import {
  selectEvent,
  selectFilter,
  selectInitFilter,
  selectReportList,
  selectSorting,
  selectPMREvent,
  selectPercentPMR,
} from 'features/adminReport/certificate/selector'
import RowItem from './RowItem'
import { EventModal, ReportZoneTab } from 'Components/Shared'
import Filter from 'Components/AdminQC/Report/Filter/CertificateFilter'
import { initPagination } from 'utils/helper'

const breadcrumbs: BreadcrumbLink[] = [
  {
    url: '/dashboard',
    title: 'Home',
    icon: '/home.png',
    active: true,
    showTitle: false,
  },
  {
    url: '/report/cert',
    title: 'รายงานตรวจส่วนกลาง',
    active: false,
    showTitle: true,
  },
]

type Props = ReduxProps & DispatchProps & RouteComponentProps

const ReportList: React.ComponentType<Props> = ({
  UserEmail,
  token,
  dataList,
  event,
  filter: currentFilter,
  initFilter,
  sorting,
  pmrEvent,
  percentPMR,
  dataExport,
  exportEvent,
}) => {
  const dispatch = useDispatch()
  const [enableModal, setEnableModal] = useState(false)
  const [enablePMR, setEnablePMRModal] = useState(false)
  const [updateMode, setUpdateMode] = useState(false)
  const [pagination, setPagination] = useState<PaginationType>(initPagination())

  useEffect(() => {
    onGetPercentPMR()
  }, [])

  useEffect(() => {
    onFindReport()
  }, [pagination])

  const onFindReport = (
    isClearFilter: boolean = false,
    isExport: boolean = false
  ) => {
    setEnableModal(true)
    const filter = isClearFilter ? initFilter : currentFilter
    const req: GetReportListReq = {
      UserEmail,
      ProjectTypeID: filter.ProjectTypeID,
      ProjectCode: filter.ProjectCode,
      Page: isExport ? 1 : pagination.Page,
      RowPerPage: isExport ? dataList.Total : pagination.RowPerPage,
    }

    if (isExport) {
      dispatch(exportReportList(token, req))
    } else {
      dispatch(getReportList(token, req))
    }
  }

  const onGetPercentPMR = () => {
    setEnablePMRModal(true)
    const req: GetPercentPMRReq = {
      UserEmail,
    }

    dispatch(getPercentPMR(token, req))
  }

  const onUpdatePercentPMR = (percent: number) => {
    setEnablePMRModal(true)
    setUpdateMode(true)
    const req: UpdatePercentPMRReq = {
      UserEmail,
      PercentPMR: percent,
    }

    dispatch(updatePercentPMR(token, req))
  }

  const onSuccess = () => {
    setEnableModal(false)
    if (updateMode) {
      setEnablePMRModal(false)
      setUpdateMode(false)
      onGetPercentPMR()
    }
  }

  const onSort = (name: string) => {
    dispatch(sortingTask({ name }))
  }

  const content = dataList.List.map((item: DataItem, index: number) => {
    return <RowItem key={index} item={item} />
  })

  return (
    <>
      <SubnavBar breadcrumbs={breadcrumbs} hideSearch={true} />
      <EventModal
        name="report-commissoning"
        event={[event, exportEvent]}
        enable={enableModal}
        successCalback={onSuccess}
      />
      <EventModal
        name="report-pmr"
        event={[pmrEvent]}
        enable={enablePMR && updateMode}
        successCalback={onSuccess}
      />
      <Menu selected={'report.zone'}>
        <MainPage>
          <Header>รายงานตรวจส่วนกลาง</Header>
          <ReportZoneTab selected="Cert" />
          <Filter
            onSearch={(isClear: boolean = false) => {
              pagination.Page = 1
              setPagination({ ...pagination })
              onFindReport(isClear)
            }}
            percentPMR={percentPMR}
            onUpdatePercent={onUpdatePercentPMR}
            isDownLoading={exportEvent.IsLoading}
            onExport={() => onFindReport(false, true)}
            exportData={dataExport}
          />
          <HeaderRow
            onSort={name => onSort(name)}
            sorting={sorting}
            items={columnsName}
          />
          {content}
          <Pagination
            currentPage={pagination.Page}
            totalRows={dataList.Total}
            perPage={pagination.RowPerPage}
            onChangeNumber={(page: number) => {
              pagination.RowPerPage = page
              setPagination({ ...pagination })
            }}
            onClickPage={(page: number) => {
              pagination.Page = page
              setPagination({ ...pagination })
            }}
          />
        </MainPage>
      </Menu>
    </>
  )
}

const MainPage = styled.div`
  padding: 40px 45px;
  width: 100%;
`

const Header = styled.div`
  margin-bottom: 27px;
  font-size: 28px;
  line-height: 1.07;
  font-family: 'DB Helvethaica X 75 Bd';
  color: #000;
`

const columnsName = [
  {
    title: 'โครงการ',
    span: 5,
    key: 'ProjectCode',
  },
  {
    title: 'ผู้รับเหมา',
    span: 5,
    key: 'VendorName',
  },
  {
    title: 'SOW',
    span: 5,
    key: 'MCVendorCode',
  },
  {
    title: 'จำนวนโซนทั้งโครงการ',
    span: 5,
    key: 'NumOfCheckUnit',
  },
  {
    title: 'อัพเดตล่าสุด',
    span: 4,
    key: 'UpdatedDate',
  },
]

const mapStateToProps = (state: RootState) => {
  return {
    dataList: selectReportList(state),
    event: selectEvent(state),
    pmrEvent: selectPMREvent(state),
    filter: selectFilter(state),
    initFilter: selectInitFilter(state),
    sorting: selectSorting(state),
    UserEmail: userEmailSelector(state),
    token: serviceTokenSelector(state),
    percentPMR: selectPercentPMR(state),
    dataExport: state.adminReportCertificate.DataExport,
    exportEvent: state.adminReportCertificate.ExportEvent,
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>
const mapDispatchToProps = {}

type DispatchProps = typeof mapDispatchToProps

export default connect(mapStateToProps, mapDispatchToProps)(ReportList)
